import { useLayoutEffect, useRef, useState, ComponentType } from 'react'

type ViewToggleOption<T extends string> = {
  title: string
  value: T
  icon: ComponentType<{ className?: string }>
  label: string
}

type ViewToggleProps<T extends string> = {
  id: string
  value: T
  onChange: (value: T) => void
  options: [ViewToggleOption<T>, ViewToggleOption<T>]
}

// Note: Figma spec uses `#1D4321` for the dark green; nearest palette token is
// `forestDeep` (#08441B). `#FBF7F3` for the cream → `linen` (#F9F5F0).
const ViewToggle = <T extends string>({
  id,
  value,
  onChange,
  options
}: ViewToggleProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const refs = [
    useRef<HTMLButtonElement>(null),
    useRef<HTMLButtonElement>(null)
  ]
  const [indicator, setIndicator] = useState({
    left: 0,
    width: 0,
    ready: false
  })

  const activeIndex = options.findIndex(o => o.value === value)

  useLayoutEffect(() => {
    const measure = () => {
      const btn = refs[activeIndex]?.current
      if (btn) {
        setIndicator({
          left: btn.offsetLeft,
          width: btn.offsetWidth,
          ready: true
        })
      }
    }
    measure()
    const observer = new ResizeObserver(measure)
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex])

  return (
    <div
      ref={containerRef}
      role='radiogroup'
      aria-labelledby={id}
      className='relative inline-flex w-full md:w-auto items-center gap-1 p-1 h-[60px] bg-white border border-forestDeep/30 rounded-lg'
    >
      <span
        aria-hidden
        className='absolute top-1 bottom-1 rounded bg-forestDeep transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]'
        style={{
          left: indicator.left,
          width: indicator.width,
          opacity: indicator.ready ? 1 : 0
        }}
      />
      {options.map((option, i) => {
        const isSelected = option.value === value
        const Icon = option.icon
        return (
          <button
            key={`${id}_${option.value}`}
            ref={refs[i]}
            type='button'
            role='radio'
            aria-checked={isSelected}
            title={option.title}
            onClick={() => onChange(option.value)}
            className={`relative z-10 flex-1 flex items-center justify-center gap-1 px-3 h-full rounded text-base font-body-rebrand transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forestDeep focus-visible:ring-offset-1 ${
              isSelected
                ? 'text-linen cursor-default pl-6 pr-6'
                : 'text-forestDeep cursor-pointer pr-6'
            }`}
          >
            <Icon className='w-6 h-6' />
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export default ViewToggle
